import React, { useState, useEffect, useRef } from 'react';
import { Mail, Loader, CheckCircle, AlertCircle, LogOut, AlertTriangle, Plus, X, Trash2, UserPlus } from 'lucide-react';
import { supabase } from '../../lib/supabase';

// Visual Editor Component
const VisualEditor = ({
    initialContent,
    onChange,
    onImageRequest
}: {
    initialContent: string,
    onChange: (content: string) => void,
    onImageRequest: (currentUrl: string, callback: (newUrl: string) => void) => void
}) => {
    const iframeRef = React.useRef<HTMLIFrameElement>(null);

    useEffect(() => {
        const iframe = iframeRef.current;
        if (!iframe) return;

        const doc = iframe.contentDocument;
        if (!doc) return;

        // Prepare content with Base URL for local assets
        let contentToWrite = initialContent || '<html><body><p>İçerik...</p></body></html>';

        // Inject <base> tag
        const baseUrl = window.location.origin + '/';
        if (contentToWrite.includes('<head>')) {
            contentToWrite = contentToWrite.replace('<head>', `<head><base href="${baseUrl}" />`);
        } else {
            contentToWrite = `<html><head><base href="${baseUrl}" /></head><body>${contentToWrite}</body></html>`;
        }

        // Write initial content
        doc.open();
        doc.write(contentToWrite);
        doc.close();

        // Make body editable
        if (doc.body) {
            doc.body.contentEditable = 'true';
            doc.body.style.minHeight = '100vh';

            // Add basic styles
            const style = doc.createElement('style');
            style.textContent = `
                body { font-family: sans-serif; padding: 20px; outline: none; }
                img { max-width: 100%; cursor: pointer; border: 2px solid transparent; transition: border-color 0.2s; }
                img:hover { border-color: #3b82f6; }
                a { cursor: pointer; border-bottom: 1px dashed #3b82f6; }
                a:hover { background-color: rgba(59, 130, 246, 0.1); }
            `;
            doc.head.appendChild(style);
        }

        // Listen for changes
        const handleInput = () => {
            let html = doc.documentElement.outerHTML;
            // Remove base tag to avoid breaking email clients
            html = html.replace(/<base[^>]*>/gi, '');
            onChange(html);
        };

        // Handle Double Click
        const handleDoubleClick = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            console.log('Double clicked:', target.tagName);

            // Image Edit
            if (target.tagName === 'IMG') {
                e.preventDefault();
                e.stopPropagation();
                const img = target as HTMLImageElement;

                onImageRequest(img.src, (newUrl) => {
                    img.src = newUrl;
                    handleInput();
                });
                return;
            }

            // Background Image Edit
            let curr = target;
            let foundBg = false;
            for (let i = 0; i < 4; i++) {
                if (!curr || curr === doc.body || curr === doc.documentElement) break;
                const style = window.getComputedStyle(curr);
                const bgImage = style.backgroundImage;
                if (bgImage && bgImage !== 'none' && bgImage.includes('url')) {
                    e.preventDefault();
                    e.stopPropagation();
                    const currentUrl = bgImage.replace(/^url\(['"]?/, '').replace(/['"]?\)$/, '');
                    foundBg = true;
                    onImageRequest(currentUrl, (newUrl) => {
                        curr.style.backgroundImage = `url('${newUrl}')`;
                        handleInput();
                    });
                    break;
                }
                curr = curr.parentElement as HTMLElement;
            }
            if (foundBg) return;

            // Link Edit
            const link = target.closest('a');
            if (link) {
                e.preventDefault();
                e.stopPropagation();
                const newHref = window.prompt('Bağlantı (Link) adresini düzenle:', link.href);
                if (newHref !== null) {
                    link.href = newHref;
                    handleInput();
                }
            }
        };

        doc.addEventListener('input', handleInput);
        doc.addEventListener('blur', handleInput);
        doc.addEventListener('keyup', handleInput);
        doc.addEventListener('dblclick', handleDoubleClick);

        return () => {
            doc.removeEventListener('input', handleInput);
            doc.removeEventListener('blur', handleInput);
            doc.removeEventListener('keyup', handleInput);
            doc.removeEventListener('dblclick', handleDoubleClick);
        };
    }, []); // Only run on mount. Parent controls re-renders via key prop.

    return (
        <div className="border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden h-[600px]">
            <iframe
                ref={iframeRef}
                className="w-full h-full bg-white"
                title="Visual Editor"
            />
        </div>
    );
};

export default function Newsletter() {
    const [token, setToken] = useState<string | null>(localStorage.getItem('inbox_token'));
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [lists, setLists] = useState<any[]>([]);

    // Login Form State
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');

    // Newsletter State
    const [selectedList, setSelectedList] = useState('');
    const [subject, setSubject] = useState('');
    const [content, setContent] = useState('');
    const [senderAccountId, setSenderAccountId] = useState('');
    const [senderEmail, setSenderEmail] = useState('');
    const [editMode, setEditMode] = useState<'visual' | 'code'>('visual');
    const [editorKey, setEditorKey] = useState(0); // Key to force re-render of VisualEditor

    // Image Upload State
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [pendingImageCallback, setPendingImageCallback] = useState<{ cb: (url: string) => void } | null>(null);

    // Template State
    const [activeTab, setActiveTab] = useState<'campaign' | 'templates'>('campaign');
    const [templates, setTemplates] = useState<any[]>([]);
    const [loadingTemplates, setLoadingTemplates] = useState(false);
    const [previewHtml, setPreviewHtml] = useState<string | null>(null);

    // Add Template Modal State
    const [showAddTemplateModal, setShowAddTemplateModal] = useState(false);
    const [newTemplateName, setNewTemplateName] = useState('');
    const [newTemplateSubject, setNewTemplateSubject] = useState('');
    const [newTemplateContent, setNewTemplateContent] = useState('');
    const [savingTemplate, setSavingTemplate] = useState(false);
    const [currentUser, setCurrentUser] = useState<any>(null);

    // Create List Modal State
    const [showCreateListModal, setShowCreateListModal] = useState(false);
    const [newListName, setNewListName] = useState('');
    const [creatingList, setCreatingList] = useState(false);
    const [groups, setGroups] = useState<any[]>([]); // To store groups
    const [selectedGroupId, setSelectedGroupId] = useState('');

    // Add Contact Modal State
    const [showAddContactModal, setShowAddContactModal] = useState(false);
    const [selectedListForContact, setSelectedListForContact] = useState<{ id: string, name: string } | null>(null);
    const [newContactEmail, setNewContactEmail] = useState('');
    const [newContactName, setNewContactName] = useState('');
    const [newContactSurname, setNewContactSurname] = useState('');
    const [addingContact, setAddingContact] = useState(false);

    useEffect(() => {
        supabase.auth.getUser().then(({ data }) => {
            setCurrentUser(data.user);
        });
    }, []);

    useEffect(() => {
        if (token) {
            fetchLists();
            fetchAccountInfo();
            fetchTemplates();
            fetchGroups(); // Fetch groups as well
        }
    }, [token]);

    const handleImageUploadRequest = (_currentUrl: string, callback: (newUrl: string) => void) => {
        setPendingImageCallback({ cb: callback });
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file || !pendingImageCallback) return;

        setLoading(true); // Reuse loading or create new one
        try {
            const fileExt = file.name.split('.').pop();
            const fileName = `${Math.random().toString(36).substring(2)}_${Date.now()}.${fileExt}`;
            const filePath = `${fileName}`;

            // Try 'newsletter_images' bucket first (created via SQL)
            const { error: uploadError } = await supabase.storage
                .from('newsletter_images')
                .upload(filePath, file);

            if (uploadError) {
                // Fallback to 'public' just in case user has that one
                const { error: uploadError2 } = await supabase.storage
                    .from('public')
                    .upload(filePath, file);

                if (uploadError2) {
                    throw new Error('Görsel yüklemesi başarısız. Lütfen "newsletter_images" adında bir storage bucket oluşturduğunuzdan emin olun.');
                }
            }

            const { data } = supabase.storage.from('newsletter_images').getPublicUrl(filePath);

            if (data.publicUrl) {
                pendingImageCallback.cb(data.publicUrl);
                setSuccessMessage('Görsel yüklendi!');
            }
        } catch (err: any) {
            console.error('Upload Error:', err);
            window.alert('Görsel yüklenemedi: ' + err.message);
        } finally {
            setLoading(false);
            setPendingImageCallback(null);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            const response = await fetch('https://useapi.useinbox.com/token', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    EmailAddress: email,
                    Password: password
                })
            });

            const data = await response.json();

            if (data.resultStatus && data.resultObject?.access_token) {
                const accessToken = data.resultObject.access_token;
                setToken(accessToken);
                localStorage.setItem('inbox_token', accessToken);
            } else {
                throw new Error(data.resultMessage || 'Giriş başarısız');
            }
        } catch (err: any) {
            setError(err.message || 'Bağlantı hatası');
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = () => {
        setToken(null);
        localStorage.removeItem('inbox_token');
        setLists([]);
        setSenderAccountId('');
    };

    const fetchLists = async () => {
        if (!token) return;
        try {
            const response = await fetch('https://useapi.useinbox.com/inbox/v1/contactlists', {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                const items = data.resultObject?.items || data.resultObject || [];
                if (Array.isArray(items)) {
                    setLists(items);
                } else {
                    setLists([]);
                }
            }
        } catch (err) {
            console.error('Liste çekme hatası:', err);
        }
    };

    const fetchAccountInfo = async () => {
        if (!token) return;
        try {
            const response = await fetch('https://useapi.useinbox.com/inbox/v1/senders', {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                const senders = data.resultObject?.items || data.resultObject || [];

                if (senders.length > 0) {
                    setSenderAccountId(senders[0].id);
                    setSenderEmail(senders[0].emailAddress || 'Seçili Gönderici');
                }
            }
        } catch (e) {
            console.log('Sender info fetch failed', e);
        }
    };

    const fetchTemplates = async () => {
        setLoadingTemplates(true);
        try {
            const localOpts: any[] = [];

            try {
                const localResponse = await fetch('/mailtemplate.html');
                if (localResponse.ok) {
                    const localHtml = await localResponse.text();
                    localOpts.push({
                        id: 'local-template-1',
                        subject: 'Varsayılan Şablon',
                        name: 'Varsayılan Şablon',
                        createTime: new Date().toISOString(),
                        htmlContent: localHtml,
                        isLocal: true
                    });
                }
            } catch (e) {
                console.error('Local template fetch failed', e);
            }

            // Fetch Supabase templates
            const { data: dbTemplates, error: dbError } = await supabase
                .from('newsletter_templates')
                .select('*')
                .order('created_at', { ascending: false });

            if (!dbError && dbTemplates) {
                const dbOpts = dbTemplates.map(t => ({
                    id: t.id,
                    subject: t.subject,
                    name: t.name,
                    createTime: t.created_at,
                    htmlContent: t.content,
                    isLocal: true,
                    isDb: true
                }));
                localOpts.push(...dbOpts);
            }

            if (token) {
                const response = await fetch('https://useapi.useinbox.com/inbox/v1/newsletters', {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                });

                if (response.ok) {
                    const data = await response.json();
                    const items = data.resultObject?.items || data.resultObject || [];
                    if (Array.isArray(items)) {
                        setTemplates([...localOpts, ...items]);
                    } else {
                        setTemplates(localOpts);
                    }
                } else {
                    setTemplates(localOpts);
                }
            } else {
                setTemplates(localOpts);
            }
        } catch (err) {
            console.error('Şablon çekme hatası:', err);
        } finally {
            setLoadingTemplates(false);
        }
    };

    const fetchGroups = async () => {
        if (!token) return;
        try {
            const response = await fetch('https://useapi.useinbox.com/inbox/v1/groups', {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                const items = data.resultObject?.items || data.resultObject || [];
                if (Array.isArray(items)) {
                    setGroups(items);
                    // Default to first group if available
                    if (items.length > 0) {
                        setSelectedGroupId(items[0].id);
                    }
                }
            }
        } catch (e) {
            console.log('Group fetch failed', e);
        }
    };

    const handleSelectTemplate = async (templateId: string, isLocal: boolean = false) => {
        setLoading(true);
        try {
            if (isLocal) {
                const template = templates.find(t => t.id === templateId);
                if (template) {
                    setContent(template.htmlContent);
                    setSubject(template.subject || subject);
                    setActiveTab('campaign');
                    setEditMode('visual');
                    setEditorKey(prev => prev + 1);
                    setSuccessMessage('Şablon yüklendi. Görsel editör üzerinden düzenleyebilirsiniz.');
                }
            } else {
                const response = await fetch(`https://useapi.useinbox.com/inbox/v1/newsletters/${templateId}`, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    }
                });

                if (response.ok) {
                    const data = await response.json();
                    if (data.resultObject?.htmlContent) {
                        setContent(data.resultObject.htmlContent);
                        setSubject(data.resultObject.subject || subject);
                        setActiveTab('campaign');
                        setEditMode('visual');
                        setEditorKey(prev => prev + 1);
                        setSuccessMessage('Şablon yüklendi. Görsel editör üzerinden düzenle.');
                    }
                }
            }
        } catch (err) {
            setError('Şablon detayları alınamadı.');
        } finally {
            setLoading(false);
        }
    };

    const handleSaveTemplate = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newTemplateName || !newTemplateContent) return;

        setSavingTemplate(true);
        try {
            const { error } = await supabase
                .from('newsletter_templates')
                .insert({
                    name: newTemplateName,
                    subject: newTemplateSubject,
                    content: newTemplateContent,
                    created_by: currentUser?.id
                });

            if (error) throw error;

            setSuccessMessage('Şablon başarıyla kaydedildi!');
            setShowAddTemplateModal(false);
            setNewTemplateName('');
            setNewTemplateSubject('');
            setNewTemplateContent('');
            fetchTemplates();
        } catch (err: any) {
            console.error('Template create error:', err);
            window.alert('Şablon oluşturulamadı: ' + err.message);
        } finally {
            setSavingTemplate(false);
        }
    };

    const handleDeleteTemplate = async (e: React.MouseEvent, id: string) => {
        e.stopPropagation();
        if (!await window.appConfirm('Bu şablonu silmek istediğinize emin misiniz?')) return;

        try {
            const { error } = await supabase
                .from('newsletter_templates')
                .delete()
                .eq('id', id);

            if (error) throw error;
            fetchTemplates();
        } catch (err: any) {
            console.error('Delete error:', err);
            window.alert('Silinemedi: ' + err.message);
        }
    };

    const handleCreateList = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newListName || !selectedGroupId) {
            window.alert('Lütfen liste adı ve grup seçiniz.');
            return;
        }

        setCreatingList(true);
        try {
            // Correct API payload based on documentation
            const response = await fetch('https://useapi.useinbox.com/inbox/v1/contactlists', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    listName: newListName,
                    groupId: selectedGroupId,
                    legislation: 0 // Default to KVKK/General
                })
            });

            const data = await response.json();

            if (data.resultStatus) {
                setSuccessMessage('Liste başarıyla oluşturuldu!');
                setShowCreateListModal(false);
                setNewListName('');
                fetchLists(); // Refresh lists
            } else {
                throw new Error(data.resultMessage || 'Liste oluşturulamadı.');
            }

        } catch (err: any) {
            console.error('List create error:', err);
            window.alert('Hata: ' + err.message);
        } finally {
            setCreatingList(false);
        }
    }

    const handleAddContact = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newContactEmail || !selectedListForContact) return;

        setAddingContact(true);
        try {
            const customFields = [];
            if (newContactName) customFields.push({ customFieldId: 'Name', value: newContactName });
            if (newContactSurname) customFields.push({ customFieldId: 'Surname', value: newContactSurname });

            const response = await fetch(`https://useapi.useinbox.com/inbox/v1/contactlists/${selectedListForContact.id}/add`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    email: newContactEmail,
                    customFields: customFields
                })
            });

            const data = await response.json();

            if (data.resultStatus) {
                setSuccessMessage('Kişi başarıyla eklendi!');
                setShowAddContactModal(false);
                setNewContactEmail('');
                setNewContactName('');
                setNewContactSurname('');
                fetchLists(); // Update list counts
            } else {
                throw new Error(data.resultMessage || 'Kişi eklenemedi.');
            }
        } catch (err: any) {
            console.error('Contact add error:', err);
            window.alert('Hata: ' + err.message);
        } finally {
            setAddingContact(false);
        }
    };

    const handleSend = async (e: React.FormEvent) => {
        e.preventDefault();
        setSending(true);
        setError(null);
        setSuccessMessage(null);

        if (!selectedList) {
            setError('Lütfen bir kişi listesi seçin.');
            setSending(false);
            return;
        }

        let contentToSend = content;
        contentToSend = contentToSend.replace(/<base[^>]*>/gi, '');
        const hasLocalImages = contentToSend.match(/src=["'](images\/|\/images\/|http:\/\/localhost)/i) ||
            contentToSend.match(/url\(['"]?(images\/|\/images\/|http:\/\/localhost)/i);

        if (hasLocalImages) {
            const confirmReplace = await window.appConfirm(
                'UYARI: Şablonunuzda yerel (bilgisayarınızdaki) görseller tespit edildi. Bu görseller e-postada GÖRÜNMEYECEKTİR.\n\n' +
                'Görselleri "Geçici Görsel (Placeholder)" ile değiştirmek ister misiniz?\n' +
                '(İptal derseniz görselleri kendiniz yüklemeniz gerekir)'
            );

            if (confirmReplace) {
                const placeholderUrl = 'https://placehold.co/600x400?text=Gorsel+Yukleyiniz';
                contentToSend = contentToSend.replace(/src=["'](images\/[^"']*|[^"']*localhost[^"']*)["']/gi, `src="${placeholderUrl}"`);
                contentToSend = contentToSend.replace(/url\(['"]?(images\/[^)'"]*|[^)'"]*localhost[^)'"]*)['"]?\)/gi, `url('${placeholderUrl}')`);
                setSuccessMessage('Yerel görseller geçici görsellerle değiştirildi.');
            } else {
                setSending(false);
                setError('Lütfen editörde tüm görsellere çift tıklayıp kendi görsellerinizi yükleyin.');
                return;
            }
        }

        try {
            const response = await fetch('https://useapi.useinbox.com/inbox/v1/campaigns/custom', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    subject: subject,
                    htmlContent: contentToSend,
                    senderAccountId: senderAccountId,
                    listType: 2,
                    lists: [selectedList],
                    language: 'tr-TR'
                })
            });

            const data = await response.json();

            if (data.resultStatus) {
                setSuccessMessage('Kampanya başarıyla oluşturuldu ve gönderim sırasına alındı!');
                setSubject('');
                setContent('');
                setSelectedList('');
            } else {
                throw new Error(data.resultMessage || 'Gönderim başarısız oldu.');
            }
        } catch (err: any) {
            console.error(err);
            setError(err.message || 'Kampanya oluşturulurken bir hata oluştu.');
        } finally {
            setSending(false);
        }
    };

    if (!token) {
        return (
            <div className="p-6 max-w-md mx-auto mt-10">
                <div className="bg-white dark:bg-gray-800 p-8 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700">
                    <div className="text-center mb-8">
                        <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                            <Mail className="w-8 h-8 text-blue-600 dark:text-blue-400" />
                        </div>
                        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Inbox Bağlantısı</h2>
                        <p className="text-gray-500 dark:text-gray-400 mt-2">
                            Newsletter yönetimi için Inbox hesabınıza giriş yapın.
                        </p>
                    </div>

                    <form onSubmit={handleLogin} className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                E-posta Adresi
                            </label>
                            <input
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                                placeholder="ornek@sirket.com"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Şifre
                            </label>
                            <input
                                type="password"
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                                placeholder="••••••••"
                            />
                        </div>

                        {error && (
                            <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 dark:bg-red-900/20 p-3 rounded-lg">
                                <AlertCircle className="w-4 h-4" />
                                {error}
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {loading ? <Loader className="w-5 h-5 animate-spin" /> : 'Giriş Yap'}
                        </button>
                    </form>
                </div>
            </div>
        );
    }

    return (
        <div className="p-6">
            <div className="flex items-center justify-between mb-8">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Newsletter Yönetimi</h1>
                    <p className="text-gray-500 dark:text-gray-400">Inbox entegrasyonu ile e-posta kampanyalarınızı yönetin.</p>
                </div>
                <button
                    onClick={handleLogout}
                    className="flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                >
                    <LogOut className="w-4 h-4" />
                    Bağlantıyı Kes
                </button>
            </div>

            {/* Tabs */}
            <div className="flex space-x-4 border-b border-gray-200 dark:border-gray-700 mb-6">
                <button
                    onClick={() => setActiveTab('campaign')}
                    className={`pb-2 px-4 font-medium transition-colors relative ${activeTab === 'campaign'
                        ? 'text-blue-600 dark:text-blue-400'
                        : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                        }`}
                >
                    Kampanya Oluştur
                    {activeTab === 'campaign' && (
                        <div className="absolute bottom-0 left-0 w-full h-0.5 bg-blue-600 dark:bg-blue-400 rounded-t-full" />
                    )}
                </button>
                <button
                    onClick={() => setActiveTab('templates')}
                    className={`pb-2 px-4 font-medium transition-colors relative ${activeTab === 'templates'
                        ? 'text-blue-600 dark:text-blue-400'
                        : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                        }`}
                >
                    Şablonlar (Inbox)
                    {activeTab === 'templates' && (
                        <div className="absolute bottom-0 left-0 w-full h-0.5 bg-blue-600 dark:bg-blue-400 rounded-t-full" />
                    )}
                </button>
            </div>

            {activeTab === 'templates' ? (
                <div className="bg-white dark:bg-gray-800 p-6 rounded-xl border border-gray-200 dark:border-gray-700">
                    <div className="flex justify-between items-center mb-6">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Hazır Şablonlar</h3>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setShowAddTemplateModal(true)}
                                className="flex items-center gap-2 px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 transition-colors"
                            >
                                <Plus className="w-4 h-4" />
                                Şablon Ekle
                            </button>
                            <button onClick={fetchTemplates} className="text-sm text-blue-500 hover:underline">Yenile</button>
                        </div>
                    </div>

                    {loadingTemplates ? (
                        <div className="text-center py-12">
                            <Loader className="w-8 h-8 animate-spin mx-auto text-blue-500 mb-3" />
                            <p className="text-gray-500">Şablonlar yükleniyor...</p>
                        </div>
                    ) : templates.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {templates.map((template) => (
                                <div key={template.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-lg transition-shadow bg-gray-50 dark:bg-gray-900/50 group">
                                    <div className="aspect-video bg-white dark:bg-gray-800 rounded-md mb-4 flex items-center justify-center border border-gray-100 dark:border-gray-700 overflow-hidden relative">
                                        <Mail className="w-12 h-12 text-gray-300 absolute" />
                                        {/* Overlay */}
                                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                                            <button
                                                onClick={() => handleSelectTemplate(template.id, template.isLocal)}
                                                className="bg-blue-600 text-white px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-blue-700"
                                            >
                                                Seç ve Düzenle
                                            </button>
                                            {template.isDb && (
                                                <button
                                                    onClick={(e) => handleDeleteTemplate(e, template.id)}
                                                    className="bg-red-600 text-white px-2 py-1.5 rounded-lg hover:bg-red-700"
                                                    title="Sil"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                    <h4 className="font-medium text-gray-900 dark:text-white truncate" title={template.subject}>
                                        {template.subject || template.name || 'İsimsiz Şablon'}
                                    </h4>
                                    <p className="text-xs text-gray-500 mt-1">
                                        {new Date(template.createTime).toLocaleDateString('tr-TR')}
                                    </p>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-12 text-gray-500">
                            <p>Hiç şablon bulunamadı. Inbox panelinden yeni bültenler oluşturarak buraya ekleyebilirsiniz.</p>
                        </div>
                    )}
                </div>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Sol Panel: Liste Seçimi */}
                    <div className="lg:col-span-1 space-y-6">
                        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl border border-gray-200 dark:border-gray-700">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Kişi Listeleri</h3>
                                <button
                                    onClick={() => setShowCreateListModal(true)}
                                    className="p-1.5 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                                    title="Yeni Liste Ekle"
                                >
                                    <Plus className="w-5 h-5" />
                                </button>
                            </div>
                            {lists.length > 0 ? (
                                <div className="space-y-2 max-h-[500px] overflow-y-auto">
                                    {lists.map((list: any) => (
                                        <div
                                            key={list.id}
                                            onClick={() => setSelectedList(list.id)}
                                            className={`p-3 border rounded-lg cursor-pointer transition-colors ${selectedList === list.id
                                                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-400'
                                                : 'border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
                                                }`}
                                        >
                                            <div className="flex items-center justify-between">
                                                <p className="font-medium text-gray-900 dark:text-white">{list.listName || 'İsimsiz Liste'}</p>
                                                <div className="flex items-center gap-2">
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setSelectedListForContact({ id: list.id, name: list.listName });
                                                            setShowAddContactModal(true);
                                                        }}
                                                        className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-full transition-colors text-gray-500 hover:text-blue-600"
                                                        title="Kişi Ekle"
                                                    >
                                                        <UserPlus className="w-4 h-4" />
                                                    </button>
                                                    {selectedList === list.id && <CheckCircle className="w-4 h-4 text-blue-500" />}
                                                </div>
                                            </div>
                                            <p className="text-sm text-gray-500">{list.count || 0} Üye</p>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-8 text-gray-500">
                                    <CheckCircle className="w-12 h-12 mx-auto text-green-500 mb-2" />
                                    <p>Bağlantı Başarılı</p>
                                    <p className="text-sm mt-2">Görünürde hiç kişi listeniz yok. Lütfen Inbox panelinden en az bir liste oluşturun.</p>
                                    <a
                                        href="https://useinbox.com/login"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-blue-500 hover:underline text-sm mt-2 inline-block"
                                    >
                                        Inbox'ta Liste Oluştur
                                    </a>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Sağ Panel: Kampanya Oluştur */}
                    <div className="lg:col-span-2">
                        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl border border-gray-200 dark:border-gray-700">
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Hızlı Gönderim</h3>
                                {senderEmail && (
                                    <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full border border-green-200">
                                        Gönderici: {senderEmail}
                                    </span>
                                )}
                            </div>

                            {!senderAccountId && (
                                <div className="mb-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg flex items-start gap-3">
                                    <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 shrink-0 mt-0.5" />
                                    <div className="text-sm text-yellow-800 dark:text-yellow-200">
                                        <p className="font-medium">Gönderici Hesabı Bulunamadı</p>
                                        <p className="mt-1">
                                            Sistem doğrulanmış gönderici hesabınızı arıyor... Eğer bu uyarı gitmiyorsa, Inbox panelinde doğrulanmış bir göndericiniz olduğundan emin olun.
                                        </p>
                                    </div>
                                </div>
                            )}

                            <form onSubmit={handleSend} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Kampanya Başlığı (Konu)
                                    </label>
                                    <input
                                        type="text"
                                        required
                                        value={subject}
                                        onChange={(e) => setSubject(e.target.value)}
                                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                                        placeholder="Örn: Ocak Ayı Bülteni"
                                    />
                                </div>

                                <div>
                                    <div className="flex justify-between items-center mb-1">
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                            İçerik
                                        </label>
                                        <div className="flex space-x-2 text-xs">
                                            <button
                                                type="button"
                                                onClick={() => setEditMode('visual')}
                                                className={`px-2 py-1 rounded ${editMode === 'visual' ? 'bg-blue-100 text-blue-700' : 'text-gray-500 hover:bg-gray-100'}`}
                                            >
                                                Görsel Editör
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setEditMode('code')}
                                                className={`px-2 py-1 rounded ${editMode === 'code' ? 'bg-blue-100 text-blue-700' : 'text-gray-500 hover:bg-gray-100'}`}
                                            >
                                                HTML Kodu
                                            </button>
                                        </div>
                                    </div>

                                    {editMode === 'visual' ? (
                                        <div>
                                            <VisualEditor
                                                key={editorKey}
                                                initialContent={content}
                                                onChange={setContent}
                                                onImageRequest={handleImageUploadRequest}
                                            />
                                            <p className="text-xs text-gray-500 mt-1 flex items-center justify-between">
                                                <span>Metinleri düzenlemek için tıklayın ve yazın.</span>
                                                <span className="font-medium text-blue-600">İpucu: Görsel veya link değiştirmek için üzerine ÇİFT TIKLAYIN.</span>
                                            </p>
                                            <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-2">
                                                <AlertTriangle className="w-4 h-4 text-yellow-600 mt-0.5 shrink-0" />
                                                <p className="text-xs text-yellow-700">
                                                    <strong>Önemli:</strong> Şablondaki varsayılan görseller (örnek resimler) sadece önizleme amaçlıdır. E-postaların düzgün görüntülenmesi ve spama düşmemesi için lütfen görsellere çift tıklayarak kendi görsellerinizi yükleyiniz.
                                                </p>
                                            </div>
                                        </div>
                                    ) : (
                                        <textarea
                                            required
                                            rows={12}
                                            value={content}
                                            onChange={(e) => setContent(e.target.value)}
                                            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                                            placeholder="<h3>Merhaba!</h3><p>Bülten içeriği buraya...</p>"
                                        />
                                    )}
                                </div>

                                {error && (
                                    <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 dark:bg-red-900/20 p-3 rounded-lg">
                                        <AlertCircle className="w-4 h-4" />
                                        {error}
                                    </div>
                                )}

                                {successMessage && (
                                    <div className="flex items-center gap-2 text-green-600 text-sm bg-green-50 dark:bg-green-900/20 p-3 rounded-lg">
                                        <CheckCircle className="w-4 h-4" />
                                        {successMessage}
                                    </div>
                                )}

                                <div className="pt-4 flex items-center justify-end">
                                    <button
                                        type="submit"
                                        disabled={sending}
                                        className="w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                                    >
                                        {sending ? <Loader className="w-5 h-5 animate-spin" /> : 'Kampanyayı Başlat'}
                                    </button>
                                </div>
                            </form>
                            <input
                                type="file"
                                ref={fileInputRef}
                                className="hidden"
                                accept="image/*"
                                onChange={handleFileChange}
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* Add Template Modal */}
            {showAddTemplateModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh]">
                        <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
                            <h3 className="font-semibold text-lg text-gray-900 dark:text-white">Yeni Şablon Ekle</h3>
                            <button
                                onClick={() => setShowAddTemplateModal(false)}
                                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="p-6 overflow-y-auto">
                            <form id="add-template-form" onSubmit={handleSaveTemplate} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Şablon Adı
                                    </label>
                                    <input
                                        type="text"
                                        required
                                        value={newTemplateName}
                                        onChange={(e) => setNewTemplateName(e.target.value)}
                                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                        placeholder="Örn: Haftalık Bülten V1"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Varsayılan Konu (Subject)
                                    </label>
                                    <input
                                        type="text"
                                        value={newTemplateSubject}
                                        onChange={(e) => setNewTemplateSubject(e.target.value)}
                                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                        placeholder="Örn: Bu haftaki gelişmeler"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        HTML İçerik
                                    </label>
                                    <textarea
                                        required
                                        value={newTemplateContent}
                                        onChange={(e) => setNewTemplateContent(e.target.value)}
                                        rows={10}
                                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-xs"
                                        placeholder="<html>...</html>"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">
                                        Buraya HTML kodunuzu yapıştırın.
                                    </p>
                                </div>
                            </form>
                        </div>

                        <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 flex justify-end gap-3">
                            <button
                                onClick={() => setShowAddTemplateModal(false)}
                                type="button"
                                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                            >
                                İptal
                            </button>
                            <button
                                form="add-template-form"
                                type="submit"
                                disabled={savingTemplate}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                            >
                                {savingTemplate ? 'Kaydediliyor...' : 'Kaydet'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Preview Modal */}
            {previewHtml && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                    <div className="bg-white dark:bg-gray-800 rounded-xl w-full max-w-4xl h-[80vh] flex flex-col shadow-2xl">
                        <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center bg-gray-50 dark:bg-gray-900 rounded-t-xl">
                            <h3 className="font-semibold text-gray-900 dark:text-white">Şablon Önizleme</h3>
                            <button onClick={() => setPreviewHtml(null)} className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
                                <span className="sr-only">Kapat</span>
                                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <div className="flex-1 overflow-auto bg-white p-4">
                            <div className="prose max-w-none" dangerouslySetInnerHTML={{ __html: previewHtml }} />
                        </div>
                        <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 rounded-b-xl flex justify-end">
                            <button
                                onClick={() => setPreviewHtml(null)}
                                className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600"
                            >
                                Kapat
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Create List Modal */}
            {showCreateListModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md overflow-hidden flex flex-col">
                        <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
                            <h3 className="font-semibold text-lg text-gray-900 dark:text-white">Yeni Kişi Listesi</h3>
                            <button
                                onClick={() => setShowCreateListModal(false)}
                                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="p-6">
                            <form id="create-list-form" onSubmit={handleCreateList} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Liste Adı
                                    </label>
                                    <input
                                        type="text"
                                        required
                                        value={newListName}
                                        onChange={(e) => setNewListName(e.target.value)}
                                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                        placeholder="Örn: Müşteriler 2024"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Grup Seçin
                                    </label>
                                    <select
                                        required
                                        value={selectedGroupId}
                                        onChange={(e) => setSelectedGroupId(e.target.value)}
                                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    >
                                        <option value="" disabled>Seçiniz</option>
                                        {groups.map(g => (
                                            <option key={g.id} value={g.id}>{g.groupName || g.name || 'İsimsiz Grup'}</option>
                                        ))}
                                    </select>
                                    <p className="text-xs text-gray-500 mt-1">
                                        Yeni liste bu grubun altına eklenecektir.
                                    </p>
                                </div>
                            </form>
                        </div>

                        <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 flex justify-end gap-3">
                            <button
                                onClick={() => setShowCreateListModal(false)}
                                type="button"
                                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                            >
                                İptal
                            </button>
                            <button
                                form="create-list-form"
                                type="submit"
                                disabled={creatingList}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                            >
                                {creatingList ? 'Oluşturuluyor...' : 'Oluştur'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add Contact Modal */}
            {showAddContactModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md overflow-hidden flex flex-col">
                        <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
                            <h3 className="font-semibold text-lg text-gray-900 dark:text-white">Kişi Ekle: {selectedListForContact?.name}</h3>
                            <button
                                onClick={() => setShowAddContactModal(false)}
                                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="p-6">
                            <form id="add-contact-form" onSubmit={handleAddContact} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        E-posta Adresi
                                    </label>
                                    <input
                                        type="email"
                                        required
                                        value={newContactEmail}
                                        onChange={(e) => setNewContactEmail(e.target.value)}
                                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                        placeholder="ornek@email.com"
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            Ad
                                        </label>
                                        <input
                                            type="text"
                                            value={newContactName}
                                            onChange={(e) => setNewContactName(e.target.value)}
                                            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                            placeholder="Ad"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                            Soyad
                                        </label>
                                        <input
                                            type="text"
                                            value={newContactSurname}
                                            onChange={(e) => setNewContactSurname(e.target.value)}
                                            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                            placeholder="Soyad"
                                        />
                                    </div>
                                </div>
                            </form>
                        </div>

                        <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 flex justify-end gap-3">
                            <button
                                onClick={() => setShowAddContactModal(false)}
                                type="button"
                                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                            >
                                İptal
                            </button>
                            <button
                                form="add-contact-form"
                                type="submit"
                                disabled={addingContact}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                            >
                                {addingContact ? 'Ekleniyor...' : 'Ekle'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
